import { getUrlSync, IActionContext } from '@msdyn365-commerce/core';
import * as React from 'react';

type TErrorSource = 'react-boundary' | 'window-error' | 'unhandled-rejection';

interface IGlobalErrorDetails {
    source: TErrorSource;
    moduleName?: string;
    moduleContext?: {
        telemetry?: Record<string, unknown>;
    };
    errorCode?: string;
    message: string;
    stack?: string;
    filename?: string;
    lineno?: number;
    colno?: number;
}

interface IGlobalErrorRuntimeConfig {
    enableHandling: boolean;
    enableTelemetry: boolean;
    enableRedirect: boolean;
    redirectPath: string;
    isEditor: boolean;
    isDebug: boolean;
}

interface IBoundaryProps {
    moduleName: string;
    children: React.ReactNode;
    moduleContext?: {
        telemetry?: Record<string, unknown>;
    };
}

interface IBoundaryState {
    hasError: boolean;
}

interface IAppInsightsClient {
    trackException(payload: { exception: Error; severityLevel?: number; properties?: Record<string, unknown> }): void;
}

interface IRequestContextConfig {
    enableGlobalClientErrorHandling?: boolean;
    enableGlobalClientErrorTelemetry?: boolean;
    enableGlobalClientErrorRedirect?: boolean;
}

interface IRequestContext {
    app?: {
        config?: IRequestContextConfig;
    };
    sitePath?: string;
    params?: {
        isEditor?: unknown;
        isDebug?: unknown;
    };
}

interface IInitialData {
    requestContext?: IActionContext & IRequestContext;
}

declare global {
    interface Window {
        __lxsGlobalErrorHandlersInstalled__?: boolean;
        __lxsLastGlobalErrorCode__?: string;
        appInsights?: IAppInsightsClient;
    }
}

const GLOBAL_ERROR_CODE_QUERY_PARAM = 'errorCode';
const GLOBAL_ERROR_CODE_STORAGE_KEY = 'lxs:globalErrorCode';

const getRuntimeConfig = (): IGlobalErrorRuntimeConfig => {
    const initialData = (window as Window & { ___initialData___?: IInitialData }).___initialData___;
    const requestContext = initialData?.requestContext;
    const appConfig = requestContext?.app?.config ?? {};
    const redirectPath = requestContext ? getUrlSync('500', requestContext) || '' : '';

    return {
        enableHandling: appConfig.enableGlobalClientErrorHandling !== false,
        enableTelemetry: appConfig.enableGlobalClientErrorTelemetry !== false,
        enableRedirect: appConfig.enableGlobalClientErrorRedirect !== false,
        redirectPath,
        isEditor: Boolean(requestContext?.params?.isEditor),
        isDebug: Boolean(requestContext?.params?.isDebug)
    };
};

const getRedirectPath = (runtimeConfig: IGlobalErrorRuntimeConfig): string => runtimeConfig.redirectPath;

const getRequestContext = (): (IActionContext & IRequestContext) | undefined => {
    const initialData = (window as Window & { ___initialData___?: IInitialData }).___initialData___;

    return initialData?.requestContext;
};

const shouldSkipRedirect = (runtimeConfig: IGlobalErrorRuntimeConfig): boolean => {
    const currentPath = window.location.pathname;
    const redirectPath = getRedirectPath(runtimeConfig);

    if (!runtimeConfig.enableRedirect) {
        return true;
    }

    if (currentPath === redirectPath) {
        return true;
    }

    if (runtimeConfig.isEditor || runtimeConfig.isDebug) {
        return true;
    }

    if (!redirectPath) {
        return true;
    }

    return false;
};

const safeSerialize = (details: IGlobalErrorDetails): string => {
    try {
        return JSON.stringify(details);
    } catch (_err) {
        return JSON.stringify({ source: details.source, message: details.message });
    }
};

const generateErrorCode = (): string => {
    const timestamp = Date.now()
        .toString(36)
        .toUpperCase();
    const randomChunk = Math.random()
        .toString(36)
        .slice(2, 8)
        .toUpperCase();

    return `LXS-${timestamp}-${randomChunk}`;
};

const persistErrorCode = (errorCode: string): void => {
    sessionStorage.setItem(GLOBAL_ERROR_CODE_STORAGE_KEY, errorCode);
};

const logToAppInsights = (error: Error, details: IGlobalErrorDetails): void => {
    const telemetryContext = details.moduleContext?.telemetry;
    const appInsights = window.appInsights;

    if (appInsights?.trackException) {
        appInsights.trackException({
            exception: error,
            severityLevel: 3,
            properties: {
                errorDetails: safeSerialize(details),
                ...(telemetryContext || {})
            }
        });
        return;
    }
};

const redirectToErrorPage = (runtimeConfig: IGlobalErrorRuntimeConfig, errorCode: string): void => {
    if (shouldSkipRedirect(runtimeConfig)) {
        return;
    }

    const requestContext = getRequestContext();
    const target = getRedirectPath(runtimeConfig);
    const sitePath = requestContext?.sitePath ?? '';
    const baseTarget = `${sitePath}${target}`;

    try {
        const targetUrl = new URL(baseTarget, window.location.origin);
        targetUrl.searchParams.set(GLOBAL_ERROR_CODE_QUERY_PARAM, errorCode);
        window.location.assign(`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`);
    } catch (_err) {
        const separator = baseTarget.includes('?') ? '&' : '?';
        window.location.assign(`${baseTarget}${separator}${GLOBAL_ERROR_CODE_QUERY_PARAM}=${encodeURIComponent(errorCode)}`);
    }
};

const normalizeUnknownError = (value: unknown): Error => {
    if (value instanceof Error) {
        return value;
    }

    if (typeof value === 'string') {
        return new Error(value);
    }

    return new Error('Unknown client error');
};

export const reportGlobalClientError = (errorLike: unknown, details: Omit<IGlobalErrorDetails, 'message' | 'stack'>): void => {
    const runtimeConfig = getRuntimeConfig();

    const normalizedError = normalizeUnknownError(errorLike);
    const mergedDetails: IGlobalErrorDetails = {
        ...details,
        errorCode: generateErrorCode(),
        message: normalizedError.message,
        stack: normalizedError.stack
    };

    persistErrorCode(mergedDetails.errorCode!);

    if (runtimeConfig.enableTelemetry) {
        logToAppInsights(normalizedError, mergedDetails);
    }

    redirectToErrorPage(runtimeConfig, mergedDetails.errorCode!);
};

export const installGlobalErrorListeners = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    const runtimeConfig = getRuntimeConfig();

    if (!runtimeConfig.enableHandling) {
        return;
    }

    window.__lxsGlobalErrorHandlersInstalled__ = true;

    window.addEventListener(
        'error',
        (event: Event) => {
            const errorEvent = event as ErrorEvent;

            if (errorEvent.error || errorEvent.message) {
                reportGlobalClientError(errorEvent.error || errorEvent.message, {
                    source: 'window-error',
                    filename: errorEvent.filename,
                    lineno: errorEvent.lineno,
                    colno: errorEvent.colno
                });
                return;
            }
        },
        true
    );

    window.addEventListener('unhandledrejection', event => {
        reportGlobalClientError(event.reason, {
            source: 'unhandled-rejection'
        });
    });
};

if (typeof window !== 'undefined') {
    installGlobalErrorListeners();
}

export class GlobalModuleErrorBoundary extends React.Component<IBoundaryProps, IBoundaryState> {
    public componentDidCatch(error: Error): void {
        reportGlobalClientError(error, {
            source: 'react-boundary',
            moduleName: this.props.moduleName,
            moduleContext: this.props.moduleContext
        });
    }

    public render(): React.ReactNode {
        return this.props.children;
    }
}

export const ErrorBoundary = <TProps extends object>(
    Component: React.ComponentType<TProps>,
    moduleName: string
): React.ComponentType<TProps> => {
    const WrappedComponent: React.FC<TProps> = props => {
        const moduleContext = (props as TProps & { context?: IBoundaryProps['moduleContext'] }).context;

        return (
            <GlobalModuleErrorBoundary moduleName={moduleName} moduleContext={moduleContext}>
                <Component {...props} />
            </GlobalModuleErrorBoundary>
        );
    };

    return WrappedComponent;
};
